import React from 'react';
import SEO from '../components/SEO';

const SECTIONS = [
  {
    title: '1. Information We Collect',
    content: [
      'Personal identification information (name, email address, phone number, date of birth)',
      'Passport or government-issued ID details required for flight bookings',
      'Payment information (processed securely; we do not store full card numbers)',
      'Search queries, travel preferences, and booking history',
      'Device information, IP address, browser type, and usage analytics',
    ],
  },
  {
    title: '2. How We Use Your Information',
    content: [
      'To process flight bookings and send confirmation emails with PNR details',
      'To communicate booking updates, flight changes, or cancellation notices',
      'To improve our search algorithms and personalize flight recommendations',
      'To comply with airline and government travel regulations',
      'To send promotional offers and newsletters (only with your consent)',
      'To detect and prevent fraud or unauthorized access',
    ],
  },
  {
    title: '3. Information Sharing',
    content: [
      'Airlines and Global Distribution Systems (GDS) — necessary to issue tickets',
      'Payment processors — to securely complete transactions',
      'Government and border control authorities — as required by law',
      'We do NOT sell, rent, or trade your personal information to third parties for marketing',
    ],
  },
  {
    title: '4. Cookies & Tracking',
    content: [
      'We use cookies to remember your search preferences and login sessions',
      'Analytics cookies (e.g., Google Analytics) help us understand site usage',
      'You can disable cookies in your browser settings; some features may be affected',
    ],
  },
  {
    title: '5. Data Security',
    content: [
      'All data transmissions are protected with 256-bit SSL/TLS encryption',
      'Payment data is processed via PCI-DSS compliant payment gateways',
      'We conduct regular security audits and vulnerability assessments',
      'Access to personal data is restricted to authorized personnel only',
    ],
  },
  {
    title: '6. Data Retention',
    content: [
      'Booking records are retained for 7 years to comply with financial regulations',
      'Account data is kept while your account is active or as required by law',
      'You may request deletion of your data by contacting support@usaflightsdeal.com',
    ],
  },
  {
    title: '7. Your Rights',
    content: [
      'Right to access — request a copy of the personal data we hold about you',
      'Right to rectification — request correction of inaccurate data',
      'Right to erasure — request deletion of your data (subject to legal obligations)',
      'Right to opt-out — unsubscribe from marketing communications at any time',
      'To exercise these rights, contact us at support@usaflightsdeal.com',
    ],
  },
  {
    title: '8. Children\'s Privacy',
    content: [
      'Our services are not directed to children under 13 years of age',
      'We do not knowingly collect personal information from children under 13',
      'If you believe a child has provided us data, please contact us immediately',
    ],
  },
  {
    title: '9. Changes to This Policy',
    content: [
      'We may update this Privacy Policy from time to time',
      'Changes will be posted on this page with an updated "Last Revised" date',
      'Continued use of our services after changes constitutes acceptance',
    ],
  },
];

export default function PrivacyPolicyPage() {
  return (
    <div>
      <SEO
        title="Privacy Policy"
        canonical="/privacy-policy"
        description="USAFlightsDeals Privacy Policy — learn how we collect, use, and protect your personal information when you book flights through our platform."
        keywords="privacy policy, data protection, personal information, usaflightsdeal privacy"
      />

      {/* Hero */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-14 sm:py-20 px-4 text-center">
        <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-sm font-medium mb-4">
          <span>🔒</span> Last Revised: June 1, 2025
        </div>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4">Privacy Policy</h1>
        <p className="text-gray-300 text-lg max-w-2xl mx-auto">
          We are committed to protecting your privacy. This policy explains what data we collect, how we use it, and your rights.
        </p>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-10 sm:py-16">
        <div className="bg-blue-50 border border-orange-200 rounded-2xl p-5 mb-10 text-sm text-orange-800">
          <strong>Summary:</strong> We collect only what's needed to book your flights, never sell your data, and protect everything with industry-standard encryption. Questions? Email <a href="mailto:support@usaflightsdeal.com" className="underline font-semibold">support@usaflightsdeal.com</a>.
        </div>

        <div className="space-y-8">
          {SECTIONS.map((section) => (
            <div key={section.title} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">{section.title}</h2>
              <ul className="space-y-2">
                {section.content.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-600 text-sm sm:text-base">
                    <span className="mt-1 w-1.5 h-1.5 rounded-full bg-orange-400 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-10 bg-gray-50 rounded-2xl border border-gray-200 p-6 text-center">
          <p className="text-gray-600 text-sm">Questions about this Privacy Policy?</p>
          <a
            href="mailto:support@usaflightsdeal.com"
            className="mt-2 inline-block bg-gradient-to-r from-blue-700 to-cyan-500 text-white font-semibold px-6 py-2.5 rounded-xl text-sm hover:opacity-90 transition-opacity"
          >
            Contact Us →
          </a>
        </div>
      </div>
    </div>
  );
}
